/** The four step bodies of first-run onboarding (name → photo → contact → language). */
import { Check, Info } from 'lucide-react';
import type { Lang, MeDTO } from '../../../shared/dto/auth';
import { AvatarEditor } from '../../components/AvatarEditor';
import { Switch, TextInput, cx } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { PrivacyLink } from '../privacy/PrivacyLink';
import { NAME_MAX, emailOk, phoneOk } from './validation';

export function NameStep({ value, onChange, showError }: { value: string; onChange: (v: string) => void; showError: boolean }) {
  const t = useT();
  return (
    <TextInput
      label={t('auth.onb.name.label')}
      name="name"
      autoComplete="name"
      autoCapitalize="words"
      enterKeyHint="next"
      maxLength={NAME_MAX}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={showError && !value.trim() ? t('auth.onb.name.required') : null}
      className="text-[19px]"
    />
  );
}

export function PhotoStep({ me }: { me: MeDTO }) {
  return (
    <div className="py-2">
      <AvatarEditor me={me} />
    </div>
  );
}

export interface ContactState {
  phone: string;
  sharePhone: boolean;
  shareEmail: boolean;
  emailPublic: string;
}

/** Contact fields the server rejected (shown even when the local check passes). */
export interface ContactInvalid {
  phone?: boolean;
  emailPublic?: boolean;
}

export function ContactStep({
  me,
  value,
  onChange,
  showErrors,
  serverInvalid = {},
}: {
  me: MeDTO;
  value: ContactState;
  onChange: (v: ContactState) => void;
  showErrors: boolean;
  serverInvalid?: ContactInvalid;
}) {
  const t = useT();
  const phoneBad = serverInvalid.phone || (showErrors && !phoneOk(value.phone));
  const emailBad = serverInvalid.emailPublic || (showErrors && !emailOk(value.emailPublic));
  const set = (patch: Partial<ContactState>) => onChange({ ...value, ...patch });
  const hasPhone = value.phone.trim().length > 0;
  return (
    <div className="flex flex-col gap-4">
      <TextInput
        label={t('auth.onb.phone')}
        type="tel"
        name="tel"
        inputMode="tel"
        autoComplete="tel"
        enterKeyHint="next"
        maxLength={24}
        value={value.phone}
        onChange={(e) => set({ phone: e.target.value, sharePhone: e.target.value.trim() ? value.sharePhone : false })}
        error={phoneBad ? t('auth.onb.phone_invalid') : null}
      />
      <div className="rounded-2xl border border-[var(--line)] px-4 py-2">
        <Switch
          checked={hasPhone && value.sharePhone}
          disabled={!hasPhone}
          onChange={(v) => set({ sharePhone: v })}
          label={t('auth.onb.share_phone')}
          description={hasPhone ? undefined : t('auth.onb.share_phone_hint')}
        />
        <div className="border-t border-[var(--line)]" />
        <Switch checked={value.shareEmail} onChange={(v) => set({ shareEmail: v })} label={t('auth.onb.share_email')} />
        {value.shareEmail ? (
          <div className="pt-1 pb-3">
            <TextInput
              label={t('auth.onb.email_public')}
              type="email"
              name="email-public"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={200}
              placeholder={me.email}
              value={value.emailPublic}
              onChange={(e) => set({ emailPublic: e.target.value })}
              hint={t('auth.onb.email_public_hint', { email: me.email })}
              error={emailBad ? t('auth.onb.email_invalid') : null}
            />
          </div>
        ) : null}
      </div>
      <p className="flex items-start gap-3 rounded-xl bg-[var(--accent-soft)] px-4 py-3 text-[15px] text-[var(--ink-2)]">
        <Info className="mt-0.5 size-5 shrink-0 text-[var(--accent)]" aria-hidden />
        <span>{t('auth.onb.admin_note')}</span>
      </p>
      {/* Consent is asked here, so the policy must be readable here (Act 90/2018, GDPR art. 13). */}
      <div className="-mt-1 flex">
        <PrivacyLink label={t('common.privacy_read')} className="-ml-2" />
      </div>
    </div>
  );
}

export function LangStep() {
  const { lang, setLang, t } = useI18n();
  const opts: { v: Lang; label: string; code: string }[] = [
    { v: 'is', label: t('auth.lang.is'), code: 'IS' },
    { v: 'en', label: t('auth.lang.en'), code: 'EN' },
  ];
  return (
    <div role="radiogroup" aria-label={t('auth.onb.lang.title')} className="grid gap-3 sm:grid-cols-2">
      {opts.map((o) => {
        const on = lang === o.v;
        return (
          <button
            key={o.v}
            type="button"
            role="radio"
            aria-checked={on}
            lang={o.v}
            onClick={() => setLang(o.v)}
            className={cx(
              'flex min-h-20 items-center gap-4 rounded-2xl border-2 px-5 text-left transition',
              on ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]',
            )}
          >
            <span
              aria-hidden
              className={cx(
                'flex size-12 shrink-0 items-center justify-center rounded-full text-[15px] font-bold',
                on ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'bg-[var(--surface-2)] text-[var(--ink-2)]',
              )}
            >
              {on ? <Check className="size-6" /> : o.code}
            </span>
            <span className="text-[21px] font-semibold">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
